import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ArrowRightLeft, Check } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Field } from "@/components/app/bits";

/**
 * Promote a change from the partner's own workspace into a client workspace.
 * Renders nothing unless the org is partner-enabled and the user can act.
 * One-way: once promoted, shows the pointer instead of the action.
 */
export default function PromoteToClient({ change, user, onDone }) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState(null);
  const [mode, setMode] = useState("existing");
  const [clientOrgId, setClientOrgId] = useState("");
  const [newName, setNewName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // partner_enabled lives on the org entry in user.orgs, not on the user itself
  const activeOrg = user?.orgs?.find((o) => o.id === user.org_id);
  const eligible = !!activeOrg?.partner_enabled && user?.type !== "partner"
    && ["owner", "change_manager"].includes(user?.role) && !change?.is_example;

  useEffect(() => {
    if (!open || targets) return;
    api.get("/partnerships/promote/targets")
      .then((r) => {
        setTargets(r.data.items);
        if (r.data.items.length) setClientOrgId(r.data.items[0].client_org_id);
        else setMode("new");
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [open, targets]);

  if (!eligible) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const body = mode === "existing"
        ? { change_id: change.id, client_org_id: clientOrgId }
        : { change_id: change.id, new_client_name: newName.trim(), contact_email: email.trim() };
      const { data } = await api.post("/partnerships/promote", body);
      setResult(data);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm" data-testid="promote-result" role="status">
        <p className="font-semibold text-foreground">
          {result.ref} created in {result.client_name}
          {result.workspace_created ? " — workspace created" : ""}.
        </p>
        <p className="mt-2 text-muted-foreground">
          Carried across: {Object.entries(result.carried).filter(([, v]) => v).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ") || "the change itself"}.
        </p>
        <p className="mt-1 text-muted-foreground">
          Stayed here: {result.not_carried.join(", ")}. Readiness measurement starts fresh in the client's workspace,
          so their trend reflects the change as they run it.
        </p>
        <Link to="/app/portfolio" className="pp-btn-ghost mt-3 text-xs">Open the portfolio</Link>
      </div>
    );
  }

  if (change.promoted_to) {
    return (
      <div className="rounded-lg border border-border bg-muted px-3 py-2 text-xs text-muted-foreground flex items-center gap-2" data-testid="promote-done">
        <Check size={14} className="text-success-600 shrink-0" aria-hidden="true" />
        <span>
          Promoted to <span className="font-semibold text-foreground">{change.promoted_to.client_name}</span> as{" "}
          <span className="font-mono">{change.promoted_to.ref}</span>. This copy stays here as your working record.
        </span>
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="pp-btn-ghost text-xs" data-testid="promote-open">
        <ArrowRightLeft size={14} aria-hidden="true" /> Promote to a client workspace
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="promote-panel">
      <div>
        <h3 className="font-semibold text-foreground text-sm">Promote to a client workspace</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Creates this change in the client's own workspace, carrying the case, stakeholders, comms, measures and risks.
          Readiness assessments, audit history and your partner notes stay here — the client's readiness trend starts
          from where they actually begin.
        </p>
      </div>

      {targets === null ? (
        <p className="text-xs text-muted-foreground">Loading your engagements…</p>
      ) : (
        <>
          <fieldset>
            <legend className="text-xs font-bold text-foreground mb-1.5">Destination</legend>
            <div className="space-y-1.5">
              {targets.length > 0 && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" name="promote-mode" value="existing" checked={mode === "existing"}
                         onChange={() => setMode("existing")} data-testid="promote-mode-existing" />
                  An existing engagement
                </label>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="promote-mode" value="new" checked={mode === "new"}
                       onChange={() => setMode("new")} data-testid="promote-mode-new" />
                A new client workspace
              </label>
            </div>
          </fieldset>

          {mode === "existing" ? (
            <Field label="Client">
              <select value={clientOrgId} onChange={(e) => setClientOrgId(e.target.value)} className="pp-input" data-testid="promote-client">
                {targets.map((t) => <option key={t.client_org_id} value={t.client_org_id}>{t.client_name}</option>)}
              </select>
            </Field>
          ) : (
            <>
              <Field label="Client name">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} className="pp-input"
                       placeholder="e.g. Lynford Manufacturing" data-testid="promote-new-name" />
              </Field>
              <Field label="Primary contact email (optional)">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pp-input" data-testid="promote-email" aria-describedby="promote-email-hint" />
              </Field>
              <p id="promote-email-hint" className="text-xs text-muted-foreground -mt-1">Optional — you invite them when you're ready, not now.</p>
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <Building2 size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
                This uses one engagement slot on your plan. The client isn't invited until you choose to.
              </p>
            </>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={submit} className="pp-btn-dark text-xs"
                    disabled={busy || (mode === "existing" ? !clientOrgId : !newName.trim())} data-testid="promote-submit">
              {busy ? "Promoting…" : "Promote"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="pp-btn-ghost text-xs" data-testid="promote-cancel">Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
